/**
 * The app's role vocabulary, and **the** administrative question the client
 * asks (`notes/identity/PLAN.md` F4; hub card `t_9837ade9`, this card
 * `t_68e3b0a5`).
 *
 * ## Why this module exists
 *
 * `owner` is the hub's own word for the account that owns it — the
 * commissioning anchor has carried it since F12 and the adoption projection
 * writes the same row — but the hub's guard used to be an **exact role match**
 * and refused an owner every admin-only route. That was fixed on the hub
 * (`otto/src/lib/hubRoles.ts`, commit `dfc249c`: `owner` implies `admin`).
 *
 * The client kept the old spelling: `stores/auth.ts` computed
 * `isAdmin = user.role === 'admin'` and five views/panels asked it directly, so
 * an account whose hub role is `owner` still saw the app **hide or disable
 * admin surfaces it may now use** — the same defect one layer up. Measured on
 * the dev hub: the anchor row is `bootstrap | role owner`, and
 * `GET /api/users` answers **200** for that session while the app's own gate
 * said "not an admin".
 *
 * ## The rule (F4), mirrored
 *
 * A role check is a **floor on one ladder**, not an exact match, and the ladder
 * is the cloud's own (`ROLE_RANK` in `otto-web/src/lib/enrollPage.ts`,
 * re-stated by `HUB_ROLE_RANK` in `otto/src/lib/hubRoles.ts`):
 *
 *     owner > admin > installer > resident > viewer
 *
 * The client cannot import either module (different repos, no shared package),
 * so this file mirrors them — and `roles.test.ts` **pins the ladder to both
 * files by reading them**, so the three copies cannot drift silently.
 *
 * Nothing else moved: `installer` does *not* clear an admin check, and a role
 * the vocabulary does not name (`''`, `'panel'`, a value a future migration
 * invents) has rank 0 and clears nothing — the client gate stays fail-closed.
 *
 * ## The older hub spellings still work
 *
 * A `users.role` written before F4 can say `guest` or `service`; they fold onto
 * the role they mean (`viewer`, `resident`), so a check answers the same thing
 * whichever spelling the row carries.
 *
 * Pure module: no store, no fetch, no Vue.
 */

/**
 * F4's canonical vocabulary, in the cloud's order — the ladder every rank below
 * mirrors. Same five values as `HUB_ROLES` (`otto/src/lib/hubRoles.ts`).
 */
export const APP_ROLES = ['owner', 'admin', 'installer', 'resident', 'viewer'] as const;

export type AppRole = (typeof APP_ROLES)[number];

/** Pre-F4 hub spellings → the canonical role they mean (F4). */
export const ROLE_ALIASES: Record<string, AppRole> = {
  guest: 'viewer',
  service: 'resident',
};

/** A role as it may appear on a row: the canonical five, or a legacy spelling. */
export type RoleName = AppRole | keyof typeof ROLE_ALIASES;

/**
 * The ladder — `otto-web/src/lib/enrollPage.ts` `ROLE_RANK`, restated by
 * `otto/src/lib/hubRoles.ts` `HUB_ROLE_RANK`. All three must agree: a client
 * that thought `installer` outranked `admin` would show a different surface
 * than the route it calls would admit.
 *
 * `roles.test.ts` asserts this equals both files' tables (read from disk).
 */
export const APP_ROLE_RANK: Record<AppRole, number> = {
  owner: 5,
  admin: 4,
  installer: 3,
  resident: 2,
  viewer: 1,
};

/** The role the hub's owner holds (F4/F12) — the anchor's and the adopter's. */
export const OWNER_ROLE: AppRole = 'owner';

/** The role an administrative check is written for. */
export const ADMIN_ROLE: AppRole = 'admin';

/**
 * What a view falls back to when it must name a role and has none — never
 * `owner` (ownership comes from the anchor or the cloud's `hubs.ownerId`) and
 * never an admin.
 */
export const DEFAULT_ROLE: AppRole = 'resident';

/**
 * The canonical role a stored value means, or `null` when it names none.
 * Case and surrounding space are ignored; `guest`/`service` fold.
 */
export function canonicalRole(role: string | null | undefined): AppRole | null {
  if (typeof role !== 'string') return null;
  const value = role.trim().toLowerCase();
  if (value === '') return null;
  if ((APP_ROLES as readonly string[]).includes(value)) return value as AppRole;
  return ROLE_ALIASES[value] ?? null;
}

/**
 * Where a role sits on the ladder, or **0** for anything the vocabulary does
 * not name. 0 is below every real role, so an unrecognised value satisfies no
 * check — the fail-closed direction.
 */
export function roleRank(role: string | null | undefined): number {
  const canonical = canonicalRole(role);
  return canonical ? APP_ROLE_RANK[canonical] : 0;
}

/**
 * Does `callerRole` clear a check written for `requiredRole`?
 *
 * At-least, on the F4 ladder: `owner` clears `admin` (the whole point — a hub
 * with an owner must be configurable by that owner, in the app as on the hub),
 * `admin` clears `admin`, and `installer`/`resident`/`viewer` do not.
 */
export function roleSatisfies(callerRole: string | null | undefined, requiredRole: string | null | undefined): boolean {
  const required = roleRank(requiredRole);
  // A check for a role the vocabulary does not name can never be satisfied —
  // otherwise a typo in an affordance table would read as "show it to everyone".
  if (required === 0) return false;
  return roleRank(callerRole) >= required;
}

/** A list of roles is a union of floors: satisfying any one of them is enough. */
export function roleSatisfiesAny(
  callerRole: string | null | undefined,
  requiredRoles: readonly (string | null | undefined)[],
): boolean {
  return requiredRoles.some((required) => roleSatisfies(callerRole, required));
}

/**
 * The administrative check, named: what every `v-if="isAdmin"` in the app means
 * (and what the hub's `authorize('admin')` means). Read it as *"is this
 * identity an admin **or better**"* — an `owner` is an admin (F4).
 */
export function isAdminRole(role: string | null | undefined): boolean {
  return roleSatisfies(role, ADMIN_ROLE);
}

/** The owner check, for the surfaces that need "does this account own the hub". */
export function isOwnerRole(role: string | null | undefined): boolean {
  return canonicalRole(role) === OWNER_ROLE;
}

/**
 * The viewer check, named (card `t_803e86a4`): the guest experience's surfaces
 * hide the settings/admin tree for this role, and for nothing above it. The
 * demo house's visitors are `viewer` rows (the welcome page's guests and any
 * account an admin invited as `viewer`/`guest`), so both spellings answer true
 * — `guest` folds onto `viewer` (F4).
 *
 * A role the vocabulary does not name is NOT a viewer: those surfaces stay as
 * they were, which is the fail-closed direction for a *hiding* rule (a typo in
 * a row's role must not silently strip a resident's menu). What hides admin
 * content is `isAdminRole`; this one only decides the guest chrome.
 */
export function isViewerRole(role: string | null | undefined): boolean {
  return canonicalRole(role) === 'viewer';
}

/**
 * Every role that answers `isAdminRole` — derived from the predicate, so a
 * surface that needs the *set* rather than the question cannot drift from the
 * ladder. Today: `owner` and `admin`, in ladder order.
 */
export const ADMIN_ROLES: readonly AppRole[] = APP_ROLES.filter(isAdminRole);
